import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const active = items[index];

  return (
    <section className="relative w-full h-[560px] md:h-[660px] overflow-hidden bg-primary-950">
      <h1 className="sr-only">天狼影院 - 最新电影电视剧大全与影视资讯</h1>

      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.hero}
            alt={item.title}
            title={`${item.title} ${item.kind === "movie" ? "电影" : "电视剧"}剧照`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-primary-950/40"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-8 xl:px-16 flex items-center">
        <div key={active.id} className="max-w-2xl animate-fade-up">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
              <i className="ri-fire-fill"></i>
              {active.kind === "movie" ? "热映电影" : "热播剧集"}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-background-50/15 px-3 py-1 text-xs font-medium text-background-50 backdrop-blur">
              <i className="ri-star-fill text-accent-400"></i>
              {active.rating.toFixed(1)} 分
            </span>
          </div>

          <h2 className="mt-4 font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight">
            {active.title}
          </h2>

          <p className="mt-3 text-sm md:text-base text-background-50/75">
            {active.genres.join(" · ")} · {active.region} · {active.year}
            {active.kind === "tv" && active.episodes ? ` · 全 ${active.episodes} 集` : ""}
          </p>

          <p className="mt-4 max-w-xl text-sm md:text-base leading-relaxed text-background-50/70 line-clamp-2">
            {active.synopsis}
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看详情
            </Link>
            <Link
              to={active.kind === "movie" ? "/category/movie" : "/category/tv"}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/15 text-background-50 text-sm font-medium backdrop-blur hover:bg-background-50/25 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-list-check-2 text-lg"></i>
              更多{active.kind === "movie" ? "电影" : "剧集"}
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-4 md:left-8 xl:left-16 z-10 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/40 hover:bg-background-50/70"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}